import { Category, FindingSeverity, FindingStatus, FindingsInsightsDto } from '@ampsec/platform-client';
import { Link } from 'react-router-dom';
import {
    AGENT_STATUS,
    DATE_TEXT,
    DEPARTMENT,
    DESCRIPTION,
    FINDING,
    FindingStatusLabelMap,
    ISSUE_TYPE_TEXT,
    NAME,
    PROVIDER,
    SEVERITY_TEXT,
    STATUS,
    TOP_RISK_CONTRIBUTORS_MAP,
} from 'Core-utils/constants/constants';
import { FINDINGS_ROUTE, USERS_DASHBOARD_ROUTE } from 'Core-utils/routes';
import { DateTimeFormatter, opacityViewClassName } from 'Core-utils/helpers/helpers';
import { Chip, CustomTooltip } from 'Components/atoms';
import { FindingChip } from 'Components/molecules';
import { ColDef } from 'ag-grid-community';
import UserNameColumnFilter from './CustomFilters/UserNameColumnFilter';
import { DepartmentColumnFilter } from '../AgToolingComplianceGrid/CustomFilters/DepartmentColumnFilter';
import TagColumnFilter from './CustomFilters/TagColumnFilter';
import SourceCellRenderer from '../InventoryGrid/cellRenderers/SourceCellRenderer';
import '../InventoryGrid/styles.scss';
import './styles.scss';
import { getMinWidthForColumn } from './AgFindingsGrid.config';
import AgentStatusCellRenderer from './cellRenderers/AgentStatusCellRenderer';
import { GoTag } from '@react-icons/all-files/go/GoTag';

const TEXT_FILTER_PARAMS = {
    buttons: ['reset'],
    debounceMs: 300,
    maxNumConditions: 1,
    suppressAndOrCondition: true,
    filterOptions: ['contains'],
};

export interface GetFindingsColumnsParams {
    formatDisplayLabel: (value: string | null | undefined) => string;
    disableFilters?: boolean;
    providerFilterOptions?: string[];
    findingKindOptions?: string[];
    contentfulProviderMap?: Record<string, { name?: string }>;
    providerLogoMap?: Record<string, string>;
    omitUserColumn?: boolean;
    showOnlyColumns?: string[];
    omitColumns?: string[];
}

const statusValues = Object.values(FindingStatus);
const categoryValues = Object.values(Category);

export function getFindingsColumns(params: GetFindingsColumnsParams): ColDef<FindingsInsightsDto>[] {
    const {
        formatDisplayLabel,
        disableFilters = false,
        providerFilterOptions = [],
        contentfulProviderMap = {},
        providerLogoMap = {},
        omitUserColumn = false,
        showOnlyColumns = [],
        omitColumns = [],
    } = params;

    const statusDisplayMap = Object.values(FindingStatus).reduce(
        (acc, status) => ({ ...acc, [status]: FindingStatusLabelMap[status] || status }),
        {} as Record<string, string>,
    );

    const categoryDisplayMap = categoryValues.reduce(
        (acc, category) => ({
            ...acc,
            [category]: TOP_RISK_CONTRIBUTORS_MAP[category] ?? category,
        }),
        {} as Record<string, string>,
    );

    const columns: ColDef<FindingsInsightsDto>[] = [
        {
            field: 'severity',
            colId: 'severity',
            headerName: SEVERITY_TEXT,
            sortable: true,
            minWidth: 110,
            filter: disableFilters ? false : 'agSetColumnFilter',
            filterParams: disableFilters ? undefined : { values: Object.values(FindingSeverity) },
            cellRenderer: (params: any) => {
                const row = params.data as FindingsInsightsDto;
                if (!row) {
                    return null;
                }
                const variant = row.severity ? 'resolved_issue_variant' : 'severity';
                return (
                    <div className={`ag-findings-grid__column-wrapper ${opacityViewClassName(row)}`}>
                        <Chip value={row.severity} variant={variant} />
                    </div>
                );
            },
        },
        {
            field: 'status',
            colId: 'finding_status',
            headerName: STATUS,
            sortable: true,
            minWidth: getMinWidthForColumn('finding_status'),
            filter: disableFilters ? false : 'agSetColumnFilter',
            filterParams: disableFilters
                ? undefined
                : {
                      values: statusValues,
                      valueFormatter: (params: any) => statusDisplayMap[params.value] ?? params.value,
                  },
            cellRenderer: (params: any) => {
                const row = params.data as FindingsInsightsDto;
                if (!row) {
                    return null;
                }
                return (
                    <div className={`${opacityViewClassName(row)}`}>
                        {FindingStatusLabelMap?.[row.status] || row.status}
                    </div>
                );
            },
        },
        {
            field: 'category',
            colId: 'finding_category',
            headerName: ISSUE_TYPE_TEXT,
            sortable: true,
            minWidth: getMinWidthForColumn('finding_category'),
            filter: disableFilters ? false : 'agSetColumnFilter',
            filterParams: disableFilters
                ? undefined
                : {
                      values: categoryValues,
                      valueFormatter: (params: { value: string }) => categoryDisplayMap[params.value] ?? params.value,
                  },
            cellRenderer: (params: any) => {
                const row = params.data as FindingsInsightsDto;
                if (!row) {
                    return null;
                }
                return (
                    <div className={opacityViewClassName(row)}>
                        <FindingChip
                            provider={row.provider_name || ''}
                            category={row.category}
                            className={opacityViewClassName(row)}
                        />
                    </div>
                );
            },
        },
        {
            field: 'provider_name',
            colId: 'provider_name',
            headerName: PROVIDER,
            sortable: true,
            minWidth: getMinWidthForColumn('provider_name'),
            filter: disableFilters ? false : 'agSetColumnFilter',
            filterParams: disableFilters
                ? undefined
                : {
                      values: providerFilterOptions.length > 0 ? providerFilterOptions.sort() : undefined,
                  },
            cellRenderer: (params: any) => {
                const row = params.data as FindingsInsightsDto;
                if (!row) {
                    return null;
                }
                const displayName =
                    contentfulProviderMap?.[row?.provider_name || 'UNKNOWN']?.name ??
                    (row.provider_name ? `${row.provider_name}` : 'UNKNOWN');
                const logo = providerLogoMap?.[row.provider_name!];
                return (
                    <div className={`ag-findings-grid__column-wrapper ${opacityViewClassName(row)}`}>
                        <img alt={`${row.provider_name} logo`} className="ag-findings-grid__provider-logo" src={logo} />
                        <div className="d-flex flex-column justify-content-center">
                            <div className={opacityViewClassName(row)}>{displayName}</div>
                        </div>
                    </div>
                );
            },
        },
        {
            field: 'title',
            colId: 'finding_title',
            headerName: FINDING,
            sortable: true,
            minWidth: getMinWidthForColumn('finding_title'),
            filter: disableFilters ? false : params.findingKindOptions?.length ? 'agSetColumnFilter' : false,
            filterParams:
                disableFilters || !params.findingKindOptions?.length
                    ? undefined
                    : {
                          values: params.findingKindOptions?.slice().sort(),
                          valueFormatter: (valueParams: any) =>
                              formatDisplayLabel(String(valueParams.value ?? '').toLowerCase()),
                      },
            valueGetter: (params: any) => (params.data as FindingsInsightsDto)?.title ?? '',
            cellRenderer: (params: any) => {
                const row = params.data as FindingsInsightsDto;
                if (!row) {
                    return null;
                }
                const displayTitle =
                    row.category === Category.CUSTOM ? row.title : formatDisplayLabel(row.title?.toLowerCase() ?? '');
                return (
                    <Link className="text-decoration-none ag-findings-grid__link" to={`${FINDINGS_ROUTE}/${row.id}`}>
                        <div className={`ag-findings-grid__column-wrapper ${opacityViewClassName(row)}`}>
                            {displayTitle}
                        </div>
                    </Link>
                );
            },
        },
        {
            field: 'kind',
            colId: 'finding_kind',
            headerName: 'Finding Kind',
            sortable: true,
            minWidth: getMinWidthForColumn('finding_kind'),
            filter: disableFilters ? false : params.findingKindOptions?.length ? 'agSetColumnFilter' : false,
            filterParams:
                disableFilters || !params.findingKindOptions?.length
                    ? undefined
                    : { values: params.findingKindOptions?.slice().sort() },
            valueGetter: (params: any) => (params.data as FindingsInsightsDto)?.title ?? '',
            cellRenderer: (params: any) => {
                const row = params.data as FindingsInsightsDto;
                if (!row) {
                    return null;
                }
                const displayTitle =
                    row.category === Category.CUSTOM ? row.title : formatDisplayLabel(row.title?.toLowerCase() ?? '');
                return (
                    <div className={`ag-findings-grid__column-wrapper ${opacityViewClassName(row)}`}>
                        {displayTitle}
                    </div>
                );
            },
        },
        {
            field: 'finding_tags',
            colId: 'finding_tags',
            headerName: 'Tags',
            sortable: false,
            minWidth: 160,
            filter: disableFilters ? false : TagColumnFilter,
            filterParams: disableFilters ? undefined : {},
            valueGetter: (params) => {
                const tags = (params.data as FindingsInsightsDto | undefined)?.finding_tags;
                return Array.isArray(tags) ? tags.filter(Boolean) : [];
            },
            cellRenderer: SourceCellRenderer,
            cellRendererParams: { maxPills: 6, icon: <GoTag /> },
        },
        ...(omitUserColumn
            ? []
            : [
                  {
                      field: 'user_name',
                      colId: 'user_name',
                      headerName: NAME,
                      sortable: true,
                      minWidth: getMinWidthForColumn('user_name'),
                      filter: disableFilters ? false : UserNameColumnFilter,
                      cellRenderer: (params: any) => {
                          const row = params.data as FindingsInsightsDto;
                          if (!row) {
                              return null;
                          }
                          return (
                              <div className={`ag-findings-grid__column-wrapper ${opacityViewClassName(row)}`}>
                                  <Link
                                      className={`${opacityViewClassName(row)} text-decoration-none ag-findings-grid__link`}
                                      to={`${USERS_DASHBOARD_ROUTE}/${row.user_id}`}
                                  >
                                      {row.user_name}
                                  </Link>
                              </div>
                          );
                      },
                  } as ColDef<FindingsInsightsDto>,
              ]),
        {
            field: 'user_department',
            colId: 'user_department',
            headerName: DEPARTMENT,
            sortable: true,
            minWidth: getMinWidthForColumn('user_department'),
            filter: disableFilters ? false : DepartmentColumnFilter,
            filterParams: disableFilters ? undefined : {},
            cellRenderer: (params: any) => {
                const row = params.data as FindingsInsightsDto;
                if (!row) {
                    return null;
                }
                return (
                    <CustomTooltip
                        id={`device-${row.id}`}
                        label={`${row.user_department ?? ''}`}
                        labelColor="primary"
                        labelVariant="subtitle3"
                        overlayPlacement="left"
                        overlayContainerClassName="d-flex flex-column justify-content-center overflow-hidden w-100"
                        tooltipClassName="color-white text-white"
                    >
                        <div className={`ag-findings-grid__column-wrapper ${opacityViewClassName(row)}`}>
                            {row.user_department ?? ''}
                        </div>
                    </CustomTooltip>
                );
            },
        },
        {
            field: 'device_display_value',
            colId: 'device_display_value',
            headerName: 'Device',
            minWidth: getMinWidthForColumn('device_display_value'),
            sortable: true,
            filter: disableFilters ? false : 'agTextColumnFilter',
            filterParams: disableFilters ? undefined : TEXT_FILTER_PARAMS,
            cellRenderer: (params: any) => {
                const row = params.data as FindingsInsightsDto;
                if (!row) {
                    return null;
                }
                return (
                    <CustomTooltip
                        id={`device-${row.id}`}
                        label={`${row.device_display_value ?? ''}`}
                        labelColor="primary"
                        labelVariant="subtitle3"
                        overlayPlacement="left"
                        overlayContainerClassName="d-flex flex-column justify-content-center overflow-hidden w-100"
                        tooltipClassName="color-white text-white"
                    >
                        <div className={`ag-findings-grid__column-wrapper ${opacityViewClassName(row)}`}>
                            {row.device_display_value ?? ''}
                        </div>
                    </CustomTooltip>
                );
            },
        },
        {
            field: 'discoveredAt',
            colId: 'discoveredAt',
            headerName: DATE_TEXT,
            sortable: true,
            filter: false,
            minWidth: getMinWidthForColumn('discoveredAt'),
            cellRenderer: (params: any) => {
                const row = params.data as FindingsInsightsDto;
                if (!row) {
                    return null;
                }
                return (
                    <div className={`ag-findings-grid__column-wrapper ${opacityViewClassName(row)}`}>
                        {DateTimeFormatter(row.discoveredAt ?? '')}
                    </div>
                );
            },
        },
        {
            field: 'description',
            colId: 'description',
            headerName: DESCRIPTION,
            sortable: false,
            filter: false,
            minWidth: getMinWidthForColumn('description'),
            cellRenderer: (params: any) => {
                const row = params.data as FindingsInsightsDto;
                if (!row) {
                    return null;
                }
                return (
                    <CustomTooltip
                        id={`description-${row.id}-${row.user_id}`}
                        label={`${row.description ?? ''}`}
                        labelColor="primary"
                        labelVariant="subtitle3"
                        overlayPlacement="left"
                        overlayContainerClassName="d-flex overflow-hidden w-100"
                        tooltipClassName="color-white text-white"
                    >
                        <div
                            className={`ag-findings-grid__column-wrapper ${opacityViewClassName(row)} d-flex overflow-hidden text-overflow-elipsis`}
                        >
                            {row.description}
                        </div>
                    </CustomTooltip>
                );
            },
        },
        {
            field: 'triggerable_agents_count',
            colId: 'triggerable_agents_count',
            headerName: AGENT_STATUS,
            sortable: false,
            minWidth: getMinWidthForColumn('triggerable_agents_count'),
            filter: false,
            cellRenderer: AgentStatusCellRenderer,
        },
    ];

    const filteredColumns = columns.filter((column) => {
        if (showOnlyColumns.length > 0) {
            if (!column.colId) {
                return false;
            }
            return showOnlyColumns.includes(column.colId);
        }
        if (omitColumns.length > 0) {
            if (!column.colId) {
                return true;
            }
            return !omitColumns.includes(column.colId);
        }
        return true;
    });
    // If showOnlyColumns is set, return those columns in the order they were specified
    const result = filteredColumns.sort((a, b) => {
        if (showOnlyColumns.length > 0) {
            return showOnlyColumns.indexOf(a.colId ?? '') - showOnlyColumns.indexOf(b.colId ?? '');
        }
        return 0;
    });
    return result;
}
